import { Check } from 'lucide-react'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { cn } from '@/shared/libs/cn'

interface LanguagePillSwitcherProps {
  languages: string[]
  activeLanguage: string
  onSelect: (lang: string) => void
  completedLanguages?: string[] | undefined
  requiredLanguage?: string | undefined
  languageLabels?: Record<string, string> | undefined
}

export function LanguagePillSwitcher({
  languages,
  activeLanguage,
  onSelect,
  completedLanguages,
  requiredLanguage,
  languageLabels,
}: LanguagePillSwitcherProps) {
  const languageLabel = useLanguageLabel()

  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-4">
      {languages.map((lang) => {
        const isComplete = completedLanguages?.includes(lang)
        const isActive = activeLanguage === lang
        const isRequired = requiredLanguage === lang
        const label = languageLabels?.[lang] ?? languageLabel(lang)
        return (
          <button
            key={lang}
            type="button"
            onClick={() => onSelect(lang)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 whitespace-nowrap border',
              isActive
                ? 'border-blue-700 bg-blue-600 text-white shadow-sm'
                : isComplete
                  ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                  : 'border-zinc-200 bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
            )}
            aria-current={isActive ? 'true' : undefined}
          >
            {label}
            {isRequired && <span className="text-[10px] leading-none">*</span>}
            {isComplete && <Check size={10} />}
          </button>
        )
      })}
    </div>
  )
}
